import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useMemo, useState, useEffect } from 'react';
import { v4 } from 'uuid';
import keycode from 'keycode';
import { useGaudiLocalization } from '../Provider/Provider.js';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef.js';
import { getIndexFromValue, getSelectedIndexesFromValue, setInputValueByRef, getActiveDescendant, findByValue } from './util.js';
import { useTypeahead } from './useTypeahead.js';

var _excluded = ["data", "defaultValue", "disabled", "id", "name", "onClick", "onChange", "onFocus", "onKeyDown", "value"];
var useMultiListbox = function useMultiListbox(_ref, ref) {
  var _ref$data = _ref.data,
      externalData = _ref$data === void 0 ? [] : _ref$data,
      defaultValue = _ref.defaultValue,
      disabled = _ref.disabled,
      nativeId = _ref.id,
      name = _ref.name,
      onClick = _ref.onClick,
      onChange = _ref.onChange,
      onFocus = _ref.onFocus,
      onKeyDown = _ref.onKeyDown,
      value = _ref.value,
      otherProps = _objectWithoutPropertiesLoose(_ref, _excluded);

  var _useGaudiLocalization = useGaudiLocalization(),
      texts = _useGaudiLocalization.texts;

  var id = useMemo(function () {
    return nativeId || v4();
  }, [nativeId]);
  var inputRef = useProxiedRef(ref);
  var data = useMemo(function () {
    return [{
      text: texts.listbox.allOption,
      value: '#ALL-OPTIONS#'
    }].concat(externalData);
  }, [externalData]);

  var _React$useState = useState(getIndexFromValue(data, value) || 0),
      currentIndex = _React$useState[0],
      setCurrentIndex = _React$useState[1];

  var _React$useState2 = useState(false),
      allOptionsSelected = _React$useState2[0],
      setAllOptionsSelected = _React$useState2[1];

  var _useTypeahead = useTypeahead({
    data: data,
    currentIndex: currentIndex
  }),
      getNextIndex = _useTypeahead.getNextIndex;

  var _React$useState3 = useState(getSelectedIndexesFromValue(data, value)),
      selectedIndexes = _React$useState3[0],
      setSelectedIndexes = _React$useState3[1];

  useEffect(function () {
    if (defaultValue && !value) {
      setInputValueByRef(inputRef.current, defaultValue.toString());
    }
  }, []);
  useEffect(function () {
    var selectableItemsCount = data.filter(function (item) {
      return !item.disabled;
    }).length - 1;

    if (selectableItemsCount === selectedIndexes.length) {
      setAllOptionsSelected(true);
    } else {
      setAllOptionsSelected(false);
    }
  }, [selectedIndexes, data]);

  var setInputValueByIndex = function setInputValueByIndex(index) {
    if (index !== undefined) {
      var option = data[index];

      if (option && !option.disabled) {
        var _inputRef$current;

        var newInputValue = '';
        var currentInputValue = (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.value;
        var currentValuesArray = (currentInputValue === null || currentInputValue === void 0 ? void 0 : currentInputValue.split(',')) || [];
        var optionAlreadySelected = currentValuesArray.findIndex(function (val) {
          return val === String(option.value);
        }) !== -1; // Handling the select_all option

        if (option.value === '#ALL-OPTIONS#') {
          if (!allOptionsSelected) {
            newInputValue = data.filter(function (option, index) {
              return index !== 0 && !option.disabled;
            }).map(function (option) {
              return option.value;
            }).join(',');
          } else {
            newInputValue = '';
          }
        } else {
          // Handling rest options
          if (optionAlreadySelected) {
            newInputValue = currentValuesArray.filter(function (val) {
              return val !== String(option.value);
            }).join(',');
          } else {
            newInputValue = currentInputValue ? currentInputValue + "," + option.value : option.value;
          }
        }

        setInputValueByRef(inputRef.current, newInputValue);
      }
    }
  };

  var handleListboxClick = function handleListboxClick(event, index) {
    event.preventDefault();

    if (!data[index].disabled) {
      setCurrentIndex(index);
      setInputValueByIndex(index);
    }

    if (onClick) {
      onClick(event);
    }
  };

  var handleListboxKeyDown = function handleListboxKeyDown(event, index) {
    var charKey = String.fromCharCode(event.keyCode);

    if (charKey.match(/(\w)/g)) {
      var nextIndex = getNextIndex(charKey);

      if (nextIndex > -1 && nextIndex !== currentIndex) {
        setCurrentIndex(nextIndex);
      }

      return;
    }

    switch (event.keyCode) {
      case keycode('space'):
        {
          event.preventDefault();
          setInputValueByIndex(index);
          break;
        }

      case keycode('enter'):
        {
          setInputValueByIndex(index);
          break;
        }
    }

    setCurrentIndex(index);

    if (onKeyDown) {
      event.persist();
      onKeyDown(event);
    }
  };

  var handleListboxFocus = function handleListboxFocus(event) {
    if (onFocus) {
      event.persist();
      onFocus(event);
    }
  };

  var handleInputChange = function handleInputChange(event) {
    event.persist();
    setSelectedIndexes(getSelectedIndexesFromValue(data, event.target.value));

    if (onChange) {
      var detail = [];
      var valuesArray = event.target.value.split(',');
      valuesArray.forEach(function (val) {
        var item = findByValue(data, val);

        if (item) {
          detail.push(item);
        }
      });
      event.detail = detail;
      onChange(event);
    }
  };

  var list = _extends({}, otherProps, {
    'aria-activedescendant': getActiveDescendant(data, currentIndex, id),
    'aria-multiselectable': true,
    data: data,
    disabled: disabled,
    id: id,
    onChange: function onChange() {
      return null;
    },
    onClick: handleListboxClick,
    onFocus: handleListboxFocus,
    onKeyDown: handleListboxKeyDown,
    scrollOnFocus: false,
    tabIndex: disabled ? -1 : otherProps.tabIndex ? otherProps.tabIndex : 0,
    value: currentIndex,
    selectedIndexes: selectedIndexes,
    multiselect: true,
    allOptionsSelected: allOptionsSelected
  });

  var input = {
    name: name,
    onChange: handleInputChange,
    ref: inputRef,
    tabIndex: -1,
    value: value !== null && value !== void 0 ? value : ''
  };
  return {
    list: list,
    input: input
  };
};

export { useMultiListbox };
//# sourceMappingURL=useMultiListbox.js.map
